import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatINR, fmtDateTime, fmtDate } from "@/lib/roles";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  PieChart, Pie, Cell,
} from "recharts";
import { Loader2, RefreshCw, Landmark, CheckCircle2, Clock, AlertTriangle, XCircle, ArrowUpRight } from "lucide-react";

const STATUS_COLORS = { settled: "#0D7650", pending: "#E8702A", failed: "#DC2626", mismatch: "#7C3AED" };
const STATUS_ICONS = { settled: CheckCircle2, pending: Clock, failed: XCircle, mismatch: AlertTriangle };
const CHANNEL_COLORS = ["#0F2942", "#E8702A", "#0D7650", "#7C3AED", "#0891B2"];

function StatusBadge({ status }) {
  const Icon = STATUS_ICONS[status] || CheckCircle2;
  const tones = {
    settled: "bg-green-100 text-green-700 border-green-300",
    pending: "bg-amber-100 text-amber-800 border-amber-300",
    failed: "bg-red-100 text-red-700 border-red-300",
    mismatch: "bg-purple-100 text-purple-700 border-purple-300",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${tones[status] || "bg-slate-100 text-slate-700"}`}>
      <Icon className="w-3 h-3" /> {status}
    </span>
  );
}

export default function Reconciliation() {
  const [summary, setSummary] = useState(null);
  const [settlements, setSettlements] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [channels, setChannels] = useState([]);
  const [txnFilter, setTxnFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [retryingId, setRetryingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, se, tx, wh, ch] = await Promise.all([
        api.get("/reconciliation/summary"),
        api.get("/reconciliation/settlements?days=7"),
        api.get("/reconciliation/transactions?limit=50"),
        api.get("/reconciliation/webhooks"),
        api.get("/reconciliation/channels"),
      ]);
      setSummary(s.data);
      setSettlements(se.data.items || []);
      setTransactions(tx.data.items || []);
      setWebhooks(wh.data.items || []);
      setChannels(ch.data.items || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load reconciliation data");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const retry = async (id) => {
    setRetryingId(id);
    try {
      await api.post(`/reconciliation/retry/${id}`);
      toast.success("Retry queued — payment will settle in next cycle");
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setRetryingId(null);
  };

  if (loading || !summary) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const filteredTxns = txnFilter === "all" ? transactions : transactions.filter((t) => t.settlement_status === txnFilter);

  return (
    <div className="space-y-6" data-testid="reconciliation-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Banking</div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold flex items-center gap-2">
            <Landmark className="w-6 h-6 text-[#0F2942] dark:text-white" /> Bank Reconciliation
          </h1>
          <p className="text-sm text-slate-500 mt-1">Real-time settlement view · {summary.bank.name} · {summary.bank.account_type} · {summary.bank.nodal_account}</p>
        </div>
        <Button variant="outline" onClick={load} data-testid="btn-refresh-recon">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Today's KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card><CardContent className="p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500">Today collected</div>
          <div className="text-2xl font-heading font-bold tabular-nums text-[#0F2942] dark:text-white mt-1">{formatINR(summary.today.collected)}</div>
          <div className="text-xs text-slate-500 mt-1">{summary.today.count} txns</div>
        </CardContent></Card>
        {["settled", "pending", "failed", "mismatch"].slice(0, 3).map((s) => (
          <Card key={s} data-testid={`kpi-today-${s}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wider text-slate-500 capitalize">Today {s}</div>
                <StatusBadge status={s} />
              </div>
              <div className="text-2xl font-heading font-bold tabular-nums mt-2" style={{ color: STATUS_COLORS[s] }}>{formatINR(summary.today.amounts[s])}</div>
              <div className="text-xs text-slate-500 mt-1">{summary.today.buckets[s]} txns</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="settlements" className="w-full">
        <TabsList>
          <TabsTrigger value="settlements" data-testid="tab-settlements">Daily settlements</TabsTrigger>
          <TabsTrigger value="transactions" data-testid="tab-transactions">Transactions</TabsTrigger>
          <TabsTrigger value="channels" data-testid="tab-channels">Payment channels</TabsTrigger>
          <TabsTrigger value="webhooks" data-testid="tab-webhooks">Bank webhooks</TabsTrigger>
        </TabsList>

        <TabsContent value="settlements">
          <Card>
            <CardHeader><CardTitle className="text-base font-heading">7-day settlement report</CardTitle></CardHeader>
            <CardContent className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={settlements}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" fontSize={11} />
                  <YAxis fontSize={11} tickFormatter={(v) => v > 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                  <Tooltip formatter={(v) => formatINR(v)} />
                  <Legend />
                  <Bar dataKey="settled" stackId="a" fill="#0D7650" name="Settled" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="pending" stackId="a" fill="#E8702A" name="Pending" />
                  <Bar dataKey="failed" stackId="a" fill="#DC2626" name="Failed" />
                  <Bar dataKey="mismatch" stackId="a" fill="#7C3AED" name="Mismatch" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card className="mt-4">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm" data-testid="settlements-table">
                <thead className="bg-slate-50 dark:bg-slate-800 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left py-2 px-4">Date</th>
                    <th className="text-right py-2 px-4">Gross</th>
                    <th className="text-right py-2 px-4">Settled</th>
                    <th className="text-right py-2 px-4">Pending</th>
                    <th className="text-right py-2 px-4">Failed</th>
                    <th className="text-right py-2 px-4">Mismatch</th>
                    <th className="text-right py-2 px-4">Txns</th>
                  </tr>
                </thead>
                <tbody>
                  {settlements.map((s, i) => (
                    <tr key={i} className="border-t">
                      <td className="py-2 px-4 font-medium">{s.date}</td>
                      <td className="py-2 px-4 text-right font-mono">{formatINR(s.gross)}</td>
                      <td className="py-2 px-4 text-right font-mono text-[#0D7650]">{formatINR(s.settled)}</td>
                      <td className="py-2 px-4 text-right font-mono text-[#E8702A]">{formatINR(s.pending)}</td>
                      <td className="py-2 px-4 text-right font-mono text-red-600">{formatINR(s.failed)}</td>
                      <td className="py-2 px-4 text-right font-mono text-purple-600">{formatINR(s.mismatch)}</td>
                      <td className="py-2 px-4 text-right text-xs tabular-nums text-slate-500">{s.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="transactions">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base font-heading">Transactions (last 50)</CardTitle>
              <Select value={txnFilter} onValueChange={setTxnFilter}>
                <SelectTrigger className="w-40" data-testid="txn-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="settled">Settled</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="mismatch">Mismatch</SelectItem>
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm" data-testid="transactions-table">
                <thead className="bg-slate-50 dark:bg-slate-800 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left py-2 px-4">Receipt</th>
                    <th className="text-left py-2 px-4">Settlement Ref</th>
                    <th className="text-left py-2 px-4">Method</th>
                    <th className="text-right py-2 px-4">Amount</th>
                    <th className="text-left py-2 px-4">Completed</th>
                    <th className="text-left py-2 px-4">Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTxns.map((t) => (
                    <tr key={t.id} className="border-t" data-testid={`recon-row-${t.id}`}>
                      <td className="py-2 px-4 font-mono text-xs">{t.receipt_number || t.payment_id}</td>
                      <td className="py-2 px-4 font-mono text-xs">{t.settlement_ref}</td>
                      <td className="py-2 px-4 uppercase text-xs">{t.method}</td>
                      <td className="py-2 px-4 text-right font-mono">{formatINR(t.amount)}</td>
                      <td className="py-2 px-4 text-xs">{fmtDateTime(t.completed_at)}</td>
                      <td className="py-2 px-4"><StatusBadge status={t.settlement_status} /></td>
                      <td className="py-2 px-4 text-right">
                        {(t.settlement_status === "failed" || t.settlement_status === "mismatch") && (
                          <Button size="sm" variant="ghost" disabled={retryingId === t.id} onClick={() => retry(t.id)} data-testid={`btn-retry-${t.id}`}>
                            {retryingId === t.id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Retry"}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {filteredTxns.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-slate-500">No transactions match this filter.</td></tr>}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="channels">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card data-testid="channels-chart">
              <CardHeader><CardTitle className="text-base font-heading">Channel share</CardTitle></CardHeader>
              <CardContent className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={channels} dataKey="amount" nameKey="channel" outerRadius={100} label={({ share }) => `${share}%`}>
                      {channels.map((_, i) => <Cell key={i} fill={CHANNEL_COLORS[i % CHANNEL_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => formatINR(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base font-heading">Channel details</CardTitle></CardHeader>
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-800 text-xs uppercase tracking-wider text-slate-500">
                    <tr><th className="text-left py-2 px-4">Channel</th><th className="text-right py-2 px-4">Amount</th><th className="text-right py-2 px-4">Txns</th><th className="text-right py-2 px-4">Share</th></tr>
                  </thead>
                  <tbody>
                    {channels.map((c) => (
                      <tr key={c.code} className="border-t">
                        <td className="py-2 px-4 font-medium">{c.channel}</td>
                        <td className="py-2 px-4 text-right font-mono">{formatINR(c.amount)}</td>
                        <td className="py-2 px-4 text-right tabular-nums text-xs">{c.count}</td>
                        <td className="py-2 px-4 text-right">
                          <div className="inline-flex items-center gap-2">
                            <div className="w-16 h-2 rounded-full bg-slate-200 overflow-hidden">
                              <div className="h-full bg-[#0F2942]" style={{ width: `${c.share}%` }} />
                            </div>
                            <span className="text-xs">{c.share}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="webhooks">
          <Card>
            <CardHeader><CardTitle className="text-base font-heading">Bank webhooks · {summary.bank.name}</CardTitle></CardHeader>
            <CardContent className="divide-y">
              {webhooks.map((w) => (
                <div key={w.id} className="py-3 flex items-center justify-between" data-testid={`webhook-${w.id}`}>
                  <div className="flex items-center gap-3">
                    <ArrowUpRight className="w-4 h-4 text-[#0D7650]" />
                    <div>
                      <div className="text-sm font-medium">{w.type}</div>
                      <div className="text-xs text-slate-500 font-mono">{w.reference}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-mono">{formatINR(w.amount)}</div>
                    <div className="text-xs text-slate-500">{fmtDateTime(w.received_at)}</div>
                  </div>
                  <Badge variant="outline">{w.status}</Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
